"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import { calcConcrete, type ConcreteResult } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import CitationSection from "@/components/CitationSection";
import RelatedCalculators from "@/components/RelatedCalculators";

// Note: Metadata export is not supported in client components.
// SEO metadata is in the parent layout or a separate server component.

const SHAPE_OPTIONS = [
  { value: "slab", label: "Slab / Patio" },
  { value: "footing", label: "Footing / Foundation Strip" },
  { value: "column", label: "Column / Post Hole (Circular)" },
];

const BAG_OPTIONS = [
  { value: 80, label: "80 lb bag (0.60 cu ft)" },
  { value: 60, label: "60 lb bag (0.45 cu ft)" },
  { value: 40, label: "40 lb bag (0.30 cu ft)" },
];

export default function CalculatorClient() {
  const [shape, setShape] = useState<"slab" | "footing" | "column">("slab");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [thickness, setThickness] = useState("");
  const [diameter, setDiameter] = useState("");
  const [height, setHeight] = useState("");
  const [bagSize, setBagSize] = useState<40 | 60 | 80>(80);
  const [result, setResult] = useState<ConcreteResult | null>(null);

  function handleCalculate() {
    const res = calcConcrete({
      shape,
      lengthFt: parseFloat(length) || 0,
      widthFt: parseFloat(width) || 0,
      thicknessIn: parseFloat(thickness) || 4,
      diameterIn: parseFloat(diameter) || 12,
      heightFt: parseFloat(height) || 1,
      bagSizeLb: bagSize,
    });
    setResult(res);
  }

  return (
    <CalculatorShell
      name="Concrete Calculator"
      description="Calculate cubic yards and bags of concrete needed for slabs, footings, and columns."
      icon="concrete"
      category="diy"
      accentColor="#6366f1"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Estimates account for standard 10% waste. Ground irregularities or form bowing may require more — confirm final quantity with your supplier before purchasing."
      proTip="Buy 1–2 extra bags if your supplier doesn't accept returns on opened concrete bags. Always round up to complete bags."
      inputsPanel={
        <div className="space-y-4">
          <SelectField
            id="shape"
            label="Shape / Project Type"
            value={shape}
            onChange={(e) => { setShape(e.target.value as typeof shape); setResult(null); }}
            options={SHAPE_OPTIONS}
          />

          {shape !== "column" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <InputField id="length" label="Length" unit="ft" type="number" min="0" step="0.1" placeholder="10" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} />
                <InputField id="width" label="Width" unit="ft" type="number" min="0" step="0.1" placeholder="10" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }} />
              </div>
              <InputField
                id="thickness"
                label="Thickness"
                unit="in"
                type="number"
                min="0"
                step="0.5"
                placeholder="4"
                value={thickness}
                onChange={(e) => { setThickness(e.target.value); setResult(null); }}
                hint="Standard slab: 4 in · Driveway: 6 in · Structural: consult engineer"
              />
            </>
          ) : (
            <>
              <InputField id="diameter" label="Column Diameter" unit="in" type="number" min="0" step="0.5" placeholder="12" value={diameter} onChange={(e) => { setDiameter(e.target.value); setResult(null); }} />
              <InputField id="height" label="Column Height" unit="ft" type="number" min="0" step="0.5" placeholder="4" value={height} onChange={(e) => { setHeight(e.target.value); setResult(null); }} />
            </>
          )}

          <SelectField
            id="bagSize"
            label="Bag Size"
            value={bagSize}
            onChange={(e) => { setBagSize(parseInt(e.target.value) as typeof bagSize); setResult(null); }}
            options={BAG_OPTIONS}
          />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Cubic Feet" value={result.cubicFeet} unit="ft³" accent />
              <ResultCard label="Cubic Yards" value={result.cubicYards} unit="yd³" accent />
            </div>
            <ResultCard
              label={`${bagSize} lb Bags (with 10% buffer)`}
              value={result.bagsWithWaste}
              unit="bags"
              highlight
              size="lg"
              sub={`${result.bagsNeeded} bags exact · +${result.bagsWithWaste - result.bagsNeeded} buffer bags`}
            />

            {/* All 3 bag sizes comparison table */}
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                All Bag Sizes Comparison
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {result.allBagSizes.map((row) => {
                  const isSelected = bagSize === row.size;
                  return (
                    <div
                      key={row.size}
                      className={`flex items-center justify-between py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                        isSelected 
                          ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30" 
                          : "border-l-3 border-l-transparent"
                      }`}
                    >
                      <span className={`text-[12.5px] font-sans ${isSelected ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                        {row.size} lb bags {isSelected && <span className="text-[9.5px] text-[var(--accent)] font-mono ml-2">(Selected)</span>}
                      </span>
                      <div className="text-right font-mono text-[13px]">
                        <span className={isSelected ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}>
                          {row.bagsWithWaste}
                        </span>
                        <span className="text-[10px] text-[var(--ink-faint)] ml-1.5 lowercase">bags</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Concrete Slab Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            When planning your project, choosing the right <Link href="/articles/how-much-concrete-do-i-need-guide/" className="text-[var(--accent)] hover:underline font-semibold">concrete mix</Link> is critical for structural integrity. For standard residential patios, sidewalks, and footings, a compressive strength of <strong>{CALCULATOR_SPECS.concrete.buyingGuide.standardSlabPsi.value} PSI</strong> is the industry standard. However, for high-load areas like <Link href="/articles/driveway-sealing-cost-guide/" className="text-[var(--accent)] hover:underline font-semibold">driveways</Link>, upgrading to <strong>{CALCULATOR_SPECS.concrete.buyingGuide.heavyDutyPsi.value} PSI</strong> is recommended to prevent cracking under vehicle weight. If using pre-mixed bags, ensure they meet <strong>{CALCULATOR_SPECS.concrete.buyingGuide.premixedBagsStandard.value}</strong> specifications. Gravel mix is ideal for projects thicker than 2 inches, whereas sand-rich mortar mix is suited for thin repairs.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Bagged concrete is practical for fence posts, small pads, steps, and patch work. For larger slabs, ready-mix delivery can be more consistent because the truck batch controls water ratio, aggregate blend, and mixing time. Once a project approaches multiple cubic yards, compare bag handling, mixer rental, delivery minimums, short-load fees, and finishing labor before buying pallets of dry mix.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Thickness should follow the load, not just habit. A walkway or shed pad can often use a thinner slab than a vehicle driveway, hot tub pad, or support for masonry. If the slab carries structural loads, supports posts, or sits on poor soil, use this calculator for volume only and have the design reviewed locally.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/concrete_material_guide.webp" alt="Finished concrete patio slab showing clean broom finish and gravel border" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/concrete_subbase_prep.webp" alt="Wooden formwork containing compacted gravel base and steel rebar mesh ready for concrete pour" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Concrete Slab Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            A successful concrete pour relies entirely on substrate preparation. Always excavate and remove loose organic soil, replacing it with a <strong>{CALCULATOR_SPECS.concrete.sitePrep.subbaseThickness.value}-inch</strong> compacted gravel subbase to ensure <Link href="/articles/basement-waterproofing-cost-guide/" className="text-[var(--accent)] hover:underline font-semibold">proper drainage</Link> and prevent settling. For structural slabs, install steel rebar (typically #3 or #4) or wire mesh to distribute tensile stress. To guide inevitable shrinkage cracks, place control joints at a maximum interval of <strong>{CALCULATOR_SPECS.concrete.sitePrep.controlJointSpacing.value} feet</strong>. Finally, keep the surface damp for at least 3 to 5 days after pouring; skipping proper hydration curing can reduce the concrete&apos;s ultimate strength by up to 50%.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Concrete failures often start before the truck arrives. Forms should be braced so they do not bow, the base should be compacted in lifts, and the slab should slope away from buildings where drainage matters. Order a little extra when the excavation bottom is uneven, because a thin low spot can become the first crack.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Plan finishing tools and labor before placement. Broom finish, edging, joint cutting, anchor placement, and curing compound all have timing windows. Adding water on the surface to make finishing easier can weaken the top layer and lead to dusting, scaling, or early wear.
          </p>
        </div>

      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.concrete.industryStandards.curingDays.standardCode!}
        sourceName={CALCULATOR_SPECS.concrete.industryStandards.curingDays.sourceName}
        sourceUrl={CALCULATOR_SPECS.concrete.industryStandards.curingDays.sourceUrl}
        paragraphs={[
          <p key="p1">
            Curing is a chemical process called hydration, not simply the evaporation of excess water. Under <b>{CALCULATOR_SPECS.concrete.industryStandards.curingDays.standardCode}</b> guidelines, concrete must be maintained in a moist condition at temperatures above 50°F for a minimum of <b>{CALCULATOR_SPECS.concrete.industryStandards.curingDays.value} days</b>. If the surface dries out prematurely, the cement hydration reaction stops, which drastically reduces the concrete&apos;s ultimate compressive strength and leaves the surface dusty, weak, and prone to scaling or cracking under load.
          </p>,
          <p key="p2">
            To achieve optimal curing, professionals use techniques such as continuous water sprinkling, ponding, covering the fresh slab with wet burlap sheets, or applying liquid membrane-forming curing compounds that seal in the moisture. Additionally, local building codes require that load-bearing concrete footings extend at least 12 inches below the local frost line. This prevents frost heave, a phenomenon where freezing ground moisture expands and lifts the concrete, causing catastrophic shifting and cracking in the structural foundation above.
          </p>,
          <p key="p3">
            Residential estimates should separate concrete volume from reinforcement, base stone, vapor barrier, forms, expansion material, finishing, saw cuts, delivery fees, and pump or buggy access. Those line items often explain why two quotes with the same cubic yards can land at very different totals.
          </p>,
          <p key="p4">
            Before ordering, verify site access, truck reach, washout location, weather window, and whether local permits are required for driveways, footings, sidewalks, or work in the public right-of-way. Concrete is time-sensitive once mixed, so access problems can become expensive quickly.
          </p>
        ]}
        keyRequirements={[
          "Minimum 7 days of continuous moist curing at temperatures above 50°F.",
          "Concrete compressive strength must reach 3,000 PSI (4,000 PSI for driveways).",
          "Control joints placed at maximum intervals of 10 feet to direct shrinkage cracks.",
          "Load-bearing footings must extend at least 12 inches below the local frost line.",
          "Compact the subbase before placement and slope exterior slabs for drainage.",
          "Separate concrete, reinforcement, forms, delivery, finishing, and curing scope.",
          "Plan truck access, washout, weather, and finishing labor before the pour starts."
        ]}
      />

      {/* Featured Guide Banner */}
      <div className="my-8 p-5 bg-[var(--surface-muted)] border border-[var(--border)] rounded-lg flex flex-col sm:flex-row items-center justify-between gap-6 card-blueprint text-left">
        <div className="flex-1 flex flex-col gap-2">
          <span className="text-[9px] font-mono text-[var(--accent)] uppercase font-bold tracking-wider">Featured Concrete Guide</span>
          <h4 className="text-base font-bold text-[var(--ink)] m-0">How Much Concrete Do I Need? (2026 Calculation Guide)</h4>
          <p className="text-xs text-[var(--ink-soft)] leading-relaxed m-0">
            Get the step-by-step math for slabs, columns, and footings, understand rebar spacing rules, and calculate how many bags to order.
          </p>
        </div>
        <Link 
          href="/articles/how-much-concrete-do-i-need-guide/" 
          className="px-4 py-2 bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90 text-xs font-mono font-bold uppercase tracking-wider rounded transition-all shrink-0 cursor-pointer text-center"
        >
          Read Concrete Guide &rarr;
        </Link>
      </div>

      <FaqSection calculatorId="concrete" />
      <RelatedCalculators calculatorId="concrete" />
    </CalculatorShell>
  );
}
